import { formatMoneyWholeAware } from 'lib/utils/format-money';
import { getMultiOrderCount } from './get-multi-order-count';
import { usdOnly } from 'lib/utils/usd-only';
import { applyShopifyFractionDiscount } from 'lib/utils/shopify-percent-discount';

type AppliedPromoCode = {
  code: string;
  value?: { percentage?: number; amount?: { amount: string } };
  recurringCycleLimit?: number | null;
} | null;

type AppliedDiscount = { price: number; discountTag?: string };

const getDiscountTag = (
  amountText: string,
  promoCode: NonNullable<AppliedPromoCode>,
): string => {
  const orderCount = getMultiOrderCount(promoCode);
  return orderCount
    ? `${amountText} off your first ${orderCount} months`
    : `${amountText} off with ${promoCode.code.toUpperCase()}`;
};

// Applies a resolved Shopify discount to a price, returning the price untouched
// when there is no code or its value doesn't apply in this market. Callers are
// responsible for checking the code covers the product first — see
// discountAppliesToProduct.
export const applyPromoCodeDiscount = (
  promoCode: AppliedPromoCode | false | undefined,
  price: number,
  currencyCode: string,
): AppliedDiscount => {
  if (!promoCode) return { price };

  const percentOff = promoCode.value?.percentage;
  if (percentOff) {
    return {
      // Floored the way Shopify charges, so the displayed price matches
      // checkout to the cent rather than landing a penny under it.
      price: Number(applyShopifyFractionDiscount(price, percentOff)),
      discountTag: getDiscountTag(
        `${Math.round(percentOff * 100)}%`,
        promoCode,
      ),
    };
  }

  const fixedAmount = usdOnly(promoCode.value?.amount?.amount, currencyCode);
  if (fixedAmount) {
    const amountOff = parseFloat(fixedAmount);
    return {
      price: Math.max(0, price - amountOff),
      discountTag: getDiscountTag(
        formatMoneyWholeAware(amountOff, currencyCode),
        promoCode,
      ),
    };
  }

  return { price };
};

// The code's value as shoppers read it ("30%", "$10"), or null when it doesn't
// apply in this market.
export const getPromoAmountText = (
  promoCode: AppliedPromoCode | false | undefined,
  currencyCode: string,
): string | null => {
  if (!promoCode) return null;

  const percentOff = promoCode.value?.percentage;
  if (percentOff) return `${Math.round(percentOff * 100)}%`;

  const fixedAmount = usdOnly(promoCode.value?.amount?.amount, currencyCode);
  if (fixedAmount) {
    return formatMoneyWholeAware(parseFloat(fixedAmount), currencyCode);
  }

  return null;
};

// Short savings badge for a code that covers the product being shown — the
// pill form of the PDP's discount tag ("Save 30%", not "30% off with X").
export const getPromoSavingsLabel = (
  promoCode: AppliedPromoCode | false | undefined,
  currencyCode: string,
): string | null => {
  const amountText = getPromoAmountText(promoCode, currencyCode);
  return amountText ? `Save ${amountText}` : null;
};
